import {Component, Plugins} from '@packages/systems/wfdl';
import {weakMemo} from '@packages/utilities/memo';
import {
  PROJECTED_GLOBALS_IMPORT,
  PROJECTED_THEME_FILENAME,
  PROJECTED_INDEX_FILENAME,
} from '../constants';
import {
  sourceFileSandboxPath,
  type SourceFilesState,
} from '../flux/sourceFilesState';
import {stripLeadingSlash} from '../paths';
import {descriptionFromContent} from '../source-files/description';

/**
 * Converts a string to a valid JavaScript identifier by replacing
 * non-alphanumeric characters with underscores.
 */
const toWebflowFriendlyIdentifier = (str: string): string => {
  const result = str.replace(/[^a-zA-Z0-9]/g, '_');
  return /^[a-zA-Z]/.test(result.charAt(0)) ? result : '__' + result;
};

const WEBFLOW_TSX_EXTENSION = '.webflow.tsx';

/** Returns true if the given filename ends with `.webflow.tsx`. */
export const isComponentFilename = (filename: string): boolean =>
  filename.endsWith(WEBFLOW_TSX_EXTENSION);

/** Converts a component name to its sandbox filename, e.g. `"component-id-1"` → `"/component-id-1.webflow.tsx"` */
export const toComponentFilename = (name: string): string =>
  `/${name}${WEBFLOW_TSX_EXTENSION}`;

/** Extracts a component name from a sandbox filename, e.g. `"/component-id-1.webflow.tsx"` → `"component-id-1"`. Returns `null` if the path isn't a `.webflow.tsx` file. */
export const fromComponentFilename = (path: string): string | null => {
  if (!path.startsWith('/') || !path.endsWith(WEBFLOW_TSX_EXTENSION))
    return null;
  return path.slice(1, -WEBFLOW_TSX_EXTENSION.length);
};

/**
 * Code Functions (M1.2) are authored as `*.webflow.function.*` source files.
 * They must never be treated as components: they run server-side, so shipping
 * one to the browser would leak handler code that may touch secrets.
 */
const WEBFLOW_FUNCTION_RE = /\.webflow\.function\.(ts|tsx|js|jsx|mts|mjs)$/;

/** What a sandbox source file is, for routing/bundling decisions. */
export type SourceFileRole = 'component' | 'function' | 'module';

/**
 * Classifies a sandbox source file. Note `.webflow.function.tsx` does not end
 * with `.webflow.tsx`, so function files already fail `isComponentFilename` —
 * the explicit `function` branch exists so call sites read as intentional
 * rather than relying on that coincidence.
 */
export const classifySourceFile = (path: string): SourceFileRole => {
  if (WEBFLOW_FUNCTION_RE.test(path)) return 'function';
  return isComponentFilename(path) ? 'component' : 'module';
};

/**
 * The function id the Code Functions bundler assigns: the basename stem before
 * the FIRST dot, e.g. `"api/items.webflow.function.ts"` → `"items"`. Ids are
 * deliberately not path-based, so two function files sharing a stem collide
 * even in different folders. Returns `null` for non-function paths.
 */
export const functionIdFromPath = (path: string): string | null => {
  if (!WEBFLOW_FUNCTION_RE.test(path)) return null;
  const basename = path.slice(path.lastIndexOf('/') + 1);
  const stem = basename.slice(0, basename.indexOf('.'));
  return stem.length > 0 ? stem : null;
};

/** One entry of {@link buildSiteFunctionCatalog}'s output. */
export type SiteFunctionCatalogEntry = {
  id: string;
  path: string;
  description?: string;
};

/**
 * Every Code Function on the site, as an index the agent can read before
 * writing: `id`, `path`, and `description` read from the file's leading block
 * comment, never from a stored field. Never a body.
 * Functions are site-scoped and shareable across AICCs (EXT-2113), so this
 * walks every `kind: 'code'` source file rather than the current component's
 * tree.
 *
 * Sorted by `id` so the catalog serializes to identical bytes across turns
 * when nothing changed -- an unstable order would otherwise defeat prompt
 * caching on every hydration.
 *
 * Two files can already share a stem (`functionIdFromPath` ignores the
 * folder). That is a pre-existing data state, not something this function
 * causes, so it dedupes deterministically -- lowest path wins -- rather than
 * emitting a collision the agent has no path to resolve.
 */
export const buildSiteFunctionCatalog = (
  files: SourceFilesState
): SiteFunctionCatalogEntry[] => {
  const byId = new Map<string, {path: string; description?: string}>();
  for (const file of Object.values(files)) {
    if (file.kind !== 'code') continue;
    // `sourceFileSandboxPath`, not `file.name`: `name` can be a bare leaf when
    // `path` (the persisted full folder path) is set, and that's what the
    // sandbox and the write-time collision check both key off.
    const path = stripLeadingSlash(sourceFileSandboxPath(file));
    const id = functionIdFromPath(path);
    if (!id) continue;
    const description = descriptionFromContent(file.content);
    const existing = byId.get(id);
    if (existing === undefined || path < existing.path) {
      byId.set(id, {path, ...(description && {description})});
    }
  }
  return Array.from(byId, ([id, entry]) => ({id, ...entry})).sort((a, b) =>
    a.id < b.id ? -1 : a.id > b.id ? 1 : 0
  );
};

/**
 * The sandbox path of an existing Code Function that already owns
 * `candidatePath`'s function id, if any -- excluding `candidatePath` itself,
 * since overwriting a function's own file is always allowed.
 *
 * `functionIdFromPath` takes the stem before the first dot and ignores the
 * folder, so two function files under different paths can silently collide
 * on id. This is the check a write goes through before that collision is
 * allowed to reach the store.
 */
export const findFunctionStemCollision = (
  candidatePath: string,
  files: SourceFilesState
): string | null => {
  const candidateId = functionIdFromPath(candidatePath);
  if (!candidateId) return null;
  for (const file of Object.values(files)) {
    if (file.kind !== 'code') continue;
    const path = stripLeadingSlash(sourceFileSandboxPath(file));
    if (path === candidatePath) continue;
    if (functionIdFromPath(path) === candidateId) return path;
  }
  return null;
};

export type ProjectedComponentFiles = Map<string, string>; // filename -> source

export const BROWSER_API_SHIMS_SOURCE = `
const isPartialBrowserEnvironment =
  typeof window !== 'undefined' &&
  typeof window.document !== 'undefined' &&
  typeof window.matchMedia !== 'function';

if (isPartialBrowserEnvironment) {
  const noop = () => {};
  const originalSetInterval = globalThis.setInterval;

  const createMediaQueryList = (query) => ({
    matches: false,
    media: query,
    onchange: null,
    addListener: noop,
    removeListener: noop,
    addEventListener: noop,
    removeEventListener: noop,
    dispatchEvent: () => false,
  });

  globalThis.matchMedia = window.matchMedia = createMediaQueryList;
  globalThis.requestAnimationFrame = window.requestAnimationFrame = () => 0;
  globalThis.cancelAnimationFrame = window.cancelAnimationFrame = noop;

  if (typeof originalSetInterval === 'function') {
    globalThis.setInterval = window.setInterval = (...args) => {
      const intervalId = originalSetInterval(...args);
      intervalId?.unref?.();
      return intervalId;
    };
  }

  // canvas.getContext('2d') returns null in the build sandbox (no canvas
  // backend), so libraries that touch a 2D context at import time crash
  // (e.g. lottie-web: "Cannot set properties of null (setting 'fillStyle')").
  // Hand back a no-op context stub so metadata collection can evaluate them.
  const CanvasElement = window.HTMLCanvasElement;
  if (typeof CanvasElement === 'function' && CanvasElement.prototype) {
    const originalGetContext = CanvasElement.prototype.getContext;
    const createStubContext = () =>
      new Proxy(
        {getImageData: () => ({data: []}), measureText: () => ({width: 0})},
        {
          get: (target, prop) => (prop in target ? target[prop] : noop),
          set: () => true,
        }
      );

    CanvasElement.prototype.getContext = function getContext(...args) {
      let context = null;
      try {
        context =
          typeof originalGetContext === 'function'
            ? originalGetContext.apply(this, args)
            : null;
      } catch {
        // jsdom throws "Not implemented" without the canvas package installed.
      }
      if (context) {
        return context;
      }
      // Only the 2D context is stubbed (the crashing path); other context
      // types keep their native result so unrelated callers are unaffected.
      return args[0] === '2d' ? createStubContext() : context;
    };
  }
}
`.trimStart();

export function addBrowserApiShimsToComponentFiles(
  files: ProjectedComponentFiles
): ProjectedComponentFiles {
  const filesWithShims = new Map(files);

  for (const [path, content] of filesWithShims) {
    if (!isComponentFilename(path)) {
      continue;
    }
    filesWithShims.set(path, `${PROJECTED_GLOBALS_IMPORT}\n${content}`);
  }

  return filesWithShims;
}

/**
 * Projects a ComponentMap to a map of filenames to source code.
 *
 * This projector converts the QualifiedMap of site components into a
 * file structure that can be used by the remote sandbox:
 * - Each component gets its own `.tsx` file
 * - An `index.ts` manifest file is generated that exports all components
 */
export const projectComponentsToFiles = weakMemo(
  (components: Component.ComponentMap): ProjectedComponentFiles => {
    let indexSource = `// This file is auto-generated by Webflow
import '.${PROJECTED_THEME_FILENAME}';
export {ClientRenderer} from '@webflow/react';
const manifest = {};\n`;

    const files = components.reduceAtPluginName(
      Plugins.name('__SitePlugin'),
      (acc, component, name) => {
        const source = Component.getSource(component);
        if (!source) return acc;

        const filename = toComponentFilename(name);
        const webflowFriendlyIdentifier = toWebflowFriendlyIdentifier(name);

        acc.set(filename, source.code);

        indexSource += `import {default as ${webflowFriendlyIdentifier}} from './${name}.webflow.tsx';\n`;
        indexSource += `manifest['${name}'] = ${webflowFriendlyIdentifier};\n`;

        return acc;
      },
      new Map() as ProjectedComponentFiles
    );

    if (files.size === 0) return files;

    indexSource += 'export default manifest;';
    files.set(PROJECTED_INDEX_FILENAME, indexSource);

    return files;
  }
);
